import { nextTick, onBeforeUnmount, shallowRef, watch } from 'vue';
import { resolveComponentRootElement } from '~/utils/ninetailed/resolveComponentRootElement';

const MAX_RESOLVE_ATTEMPTS = 5;

/**
 * Binds a stable component instance ref and defers DOM root resolution until
 * after mount, async hydration, and transient ref(null) re-renders settle.
 */
export function useTrackedComponentRoot() {
  const componentInstanceRef = shallowRef<unknown>(null);
  const trackedElement = shallowRef<Element | null>(null);

  let resolveGeneration = 0;
  let rafId: number | null = null;

  const clearPendingResolve = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const scheduleResolve = () => {
    const generation = ++resolveGeneration;

    const attemptResolve = (attempt: number) => {
      if (generation !== resolveGeneration) {
        return;
      }

      const instance = componentInstanceRef.value;
      if (!instance) {
        if (attempt < MAX_RESOLVE_ATTEMPTS) {
          rafId = requestAnimationFrame(() => attemptResolve(attempt + 1));
        }
        return;
      }

      const element = resolveComponentRootElement(instance);
      if (element) {
        trackedElement.value = element;
        return;
      }

      if (attempt < MAX_RESOLVE_ATTEMPTS) {
        rafId = requestAnimationFrame(() => attemptResolve(attempt + 1));
      }
    };

    clearPendingResolve();
    void nextTick(() => {
      if (generation !== resolveGeneration) {
        return;
      }
      attemptResolve(0);
    });
  };

  watch(componentInstanceRef, scheduleResolve, { flush: 'post' });

  onBeforeUnmount(() => {
    resolveGeneration += 1;
    clearPendingResolve();
    trackedElement.value = null;
  });

  return {
    componentInstanceRef,
    trackedElement
  };
}
